
import React, { useState, useMemo } from 'react';
import { Word, UserProgress, User, UserRole } from '../types';

interface Props {
  words: Word[];
  progress: UserProgress[];
  users: User[];
  onAddUser: (user: User) => void;
  onDeleteUser: (userId: string) => void;
  onTogglePriority: (wordId: string) => void;
}

const AdminPanel: React.FC<Props> = ({ words, progress, users, onAddUser, onDeleteUser, onTogglePriority }) => {
  const [activeTab, setActiveTab] = useState<'stats' | 'users'>('stats');
  const [filterUnit, setFilterUnit] = useState<string>('All');
  const [selectedUserId, setSelectedUserId] = useState<string>('All');
  const [showOnlyPriority, setShowOnlyPriority] = useState<boolean>(false);
  
  // 新用户表单
  const [newUsername, setNewUsername] = useState('');
  const [newUserPassword, setNewUserPassword] = useState('');

  const units = useMemo(() => ['All', ...Array.from(new Set(words.map(w => w.unit)))], [words]);
  
  const studentList = useMemo(() => users.filter(u => u.role === UserRole.STUDENT), [users]);

  const displayWords = useMemo(() => {
    return words.filter(w => {
      const unitMatch = filterUnit === 'All' || w.unit === filterUnit;
      const priorityMatch = !showOnlyPriority || w.isPriority;
      return unitMatch && priorityMatch;
    });
  }, [words, filterUnit, showOnlyPriority]);

  const getWordStats = (wordId: string) => {
    let relevantProgress = progress.filter(p => p.wordId === wordId);
    if (selectedUserId !== 'All') {
      relevantProgress = relevantProgress.filter(p => p.userId === selectedUserId);
    }
    const totalWrong = relevantProgress.reduce((sum, p) => sum + p.wrongCount, 0);
    const affectedUsers = relevantProgress
      .filter(p => p.wrongCount > 0)
      .map(p => ({ name: p.username, count: p.wrongCount }));
    
    return { totalWrong, affectedUsers };
  };

  const handleCreateUser = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newUsername || !newUserPassword) return;
    onAddUser({
      id: Date.now().toString(),
      username: newUsername,
      password: newUserPassword,
      role: UserRole.STUDENT
    });
    setNewUsername('');
    setNewUserPassword('');
  };

  // 导出为 .doc (Word 兼容 HTML 格式)
  const handleExportDoc = (type: 'current' | 'priority') => {
    const targetWords = type === 'priority' ? words.filter(w => w.isPriority) : displayWords;
    const title = type === 'priority' ? "重点掌握单词清单" : "单词复习统计报表";
    
    let htmlContent = `
      <html xmlns:o='urn:schemas-microsoft-com:office:office' xmlns:w='urn:schemas-microsoft-com:office:word' xmlns='http://www.w3.org/TR/REC-html40'>
      <head><meta charset='utf-8'><title>${title}</title>
      <style>
        body { font-family: 'SimSun', serif; }
        table { border-collapse: collapse; width: 100%; }
        th, td { border: 1px solid black; padding: 8px; text-align: left; }
        .header { text-align: center; font-size: 24px; font-weight: bold; margin-bottom: 20px; }
      </style>
      </head>
      <body>
        <div class="header">${title}</div>
        <p>导出时间: ${new Date().toLocaleString()}</p>
        ${selectedUserId !== 'All' ? `<p>筛选学生: ${users.find(u => u.id === selectedUserId)?.username}</p>` : ''}
        <table>
          <thead>
            <tr style="background-color: #f2f2f2;">
              <th>编号</th>
              <th>单词</th>
              <th>词性</th>
              <th>中文释义</th>
              <th>单元</th>
              <th>错误次数</th>
            </tr>
          </thead>
          <tbody>
            ${targetWords.map((w, i) => {
              const stats = getWordStats(w.id);
              return `
                <tr>
                  <td>${i + 1}</td>
                  <td><b>${w.english}</b></td>
                  <td>${w.pos}</td>
                  <td>${w.chinese}</td>
                  <td>${w.unit}</td>
                  <td>${stats.totalWrong}</td>
                </tr>
              `;
            }).join('')}
          </tbody>
        </table>
      </body>
      </html>
    `;

    const blob = new Blob([htmlContent], { type: 'application/msword' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${title}_${new Date().toLocaleDateString()}.doc`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="p-8 max-w-7xl mx-auto bg-slate-50 min-h-screen">
      <header className="flex flex-col md:flex-row justify-between items-start md:items-center mb-10 gap-6">
        <div>
          <h1 className="text-4xl font-black text-slate-900 tracking-tight">管理工作台</h1>
          <p className="text-slate-500 font-medium mt-1">管理学生账号并导出复习资料</p>
        </div>
        
        <div className="flex bg-white p-1 rounded-2xl shadow-sm border border-slate-100">
          <button 
            onClick={() => setActiveTab('stats')}
            className={`px-6 py-2 rounded-xl font-bold transition-all ${activeTab === 'stats' ? 'bg-blue-600 text-white shadow-md' : 'text-slate-400 hover:text-slate-600'}`}
          >
            数据报表
          </button>
          <button 
            onClick={() => setActiveTab('users')}
            className={`px-6 py-2 rounded-xl font-bold transition-all ${activeTab === 'users' ? 'bg-blue-600 text-white shadow-md' : 'text-slate-400 hover:text-slate-600'}`}
          >
            学生管理
          </button>
        </div>
      </header>

      {activeTab === 'stats' ? (
        <>
          <section className="bg-white p-6 rounded-3xl shadow-sm border border-slate-100 mb-8 flex flex-wrap gap-4 items-end">
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-black text-slate-400 uppercase tracking-tighter ml-1">单元筛选</label>
              <select 
                className="bg-slate-50 border-none px-4 py-3 rounded-xl font-bold text-slate-700 outline-none"
                value={filterUnit}
                onChange={(e) => setFilterUnit(e.target.value)}
              >
                {units.map(u => <option key={u} value={u}>{u === 'All' ? '全部单元' : u}</option>)}
              </select>
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-black text-slate-400 uppercase tracking-tighter ml-1">学生筛选</label>
              <select 
                className="bg-slate-50 border-none px-4 py-3 rounded-xl font-bold text-slate-700 outline-none"
                value={selectedUserId}
                onChange={(e) => setSelectedUserId(e.target.value)}
              >
                <option value="All">所有学生 (课堂总结)</option>
                {studentList.map(u => <option key={u.id} value={u.id}>{u.username}</option>)}
              </select>
            </div>

            <button 
              onClick={() => setShowOnlyPriority(!showOnlyPriority)}
              className={`px-6 py-3 rounded-xl font-bold transition-all flex items-center gap-2 ${
                showOnlyPriority ? 'bg-amber-100 text-amber-700 border-2 border-amber-200' : 'bg-white text-slate-400 border-2 border-slate-100'
              }`}
            >
              <i className="fas fa-star"></i> 只看重点
            </button>

            <div className="ml-auto flex gap-2">
              <button 
                onClick={() => handleExportDoc('current')}
                className="bg-indigo-600 hover:bg-indigo-700 text-white px-5 py-3 rounded-xl flex items-center gap-2 transition-all font-bold"
              >
                <i className="fas fa-file-word"></i> 导出视图 (.doc)
              </button>
              <button 
                onClick={() => handleExportDoc('priority')}
                className="bg-amber-500 hover:bg-amber-600 text-white px-5 py-3 rounded-xl flex items-center gap-2 transition-all font-bold"
              >
                <i className="fas fa-star"></i> 导出重点 (.doc)
              </button>
            </div>
          </section>

          <div className="bg-white rounded-[2rem] shadow-sm border border-slate-100 overflow-hidden">
            <table className="w-full text-left">
              <thead>
                <tr className="bg-slate-50/50">
                  <th className="px-8 py-4 text-xs font-black text-slate-400 uppercase">重点</th>
                  <th className="px-8 py-4 text-xs font-black text-slate-400 uppercase">单词</th>
                  <th className="px-8 py-4 text-xs font-black text-slate-400 uppercase">单元</th>
                  <th className="px-8 py-4 text-xs font-black text-slate-400 uppercase">错误统计</th>
                  <th className="px-8 py-4 text-xs font-black text-slate-400 uppercase">涉及学生</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {displayWords.map(word => {
                  const stats = getWordStats(word.id);
                  return (
                    <tr key={word.id} className="hover:bg-slate-50 transition-colors">
                      <td className="px-8 py-5">
                        <button 
                          onClick={() => onTogglePriority(word.id)}
                          className={`text-xl ${word.isPriority ? 'text-amber-400' : 'text-slate-200'}`}
                        >
                          <i className="fas fa-star"></i>
                        </button>
                      </td>
                      <td className="px-8 py-5">
                        <div>
                          <p className="text-lg font-black text-slate-800">{word.english}</p>
                          <p className="text-xs font-bold text-indigo-500">{word.pos} {word.chinese}</p>
                        </div>
                      </td>
                      <td className="px-8 py-5"><span className="text-xs font-black text-slate-500">{word.unit}</span></td>
                      <td className="px-8 py-5"><span className="font-bold text-red-500">{stats.totalWrong} 次</span></td>
                      <td className="px-8 py-5">
                        <div className="flex flex-wrap gap-1">
                          {stats.affectedUsers.map(u => (
                            <span key={u.name} className="bg-blue-50 text-blue-600 text-[10px] px-2 py-0.5 rounded font-bold">{u.name}({u.count})</span>
                          ))}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      ) : (
        <section className="grid md:grid-cols-3 gap-8">
          <div className="bg-white p-8 rounded-[2rem] shadow-sm border border-slate-100 h-fit">
            <h3 className="text-xl font-bold mb-6">创建学生账号</h3>
            <form onSubmit={handleCreateUser} className="space-y-4">
              <input 
                type="text" 
                placeholder="用户名" 
                className="w-full bg-slate-50 p-4 rounded-xl border-none outline-none font-bold text-slate-700"
                value={newUsername}
                onChange={(e) => setNewUsername(e.target.value)}
              />
              <input 
                type="password" 
                placeholder="初始密码" 
                className="w-full bg-slate-50 p-4 rounded-xl border-none outline-none font-bold text-slate-700"
                value={newUserPassword}
                onChange={(e) => setNewUserPassword(e.target.value)}
              />
              <button className="w-full bg-blue-600 text-white py-4 rounded-xl font-bold shadow-lg shadow-blue-100">确认创建</button>
            </form>
          </div>

          <div className="md:col-span-2 bg-white rounded-[2rem] shadow-sm border border-slate-100 overflow-hidden">
            <table className="w-full text-left">
              <thead>
                <tr className="bg-slate-50">
                  <th className="px-8 py-4 text-xs font-black text-slate-400">用户名</th>
                  <th className="px-8 py-4 text-xs font-black text-slate-400">当前密码</th>
                  <th className="px-8 py-4 text-xs font-black text-slate-400">操作</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {studentList.map(u => (
                  <tr key={u.id}>
                    <td className="px-8 py-5 font-bold text-slate-700">{u.username}</td>
                    <td className="px-8 py-5 text-slate-400 font-mono">{u.password}</td>
                    <td className="px-8 py-5">
                      <button 
                        onClick={() => onDeleteUser(u.id)}
                        className="text-red-400 hover:text-red-600 font-bold text-sm"
                      >
                        删除账号
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  );
};

export default AdminPanel;
